import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { PencilSimple, Check, X, ArrowCounterClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Build metadata pill for the Database tab. Shows the version, current
 * git SHA + branch, commit-authored-at relative time, and last-update
 * timestamp so on-site staff can confirm which release is on the box
 * without SSH access.
 *
 * Admins can also inline-edit the app_version — the override is stored
 * in `system_config.app_version` (shared MongoDB, so preview + prod
 * stay in sync), which lets you bump version numbers on cosmetic
 * releases without spending credits on a code change.
 */
const BuildInfoChip = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [info, setInfo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/system/version`)
      .then((r) => setInfo(r.data))
      .catch(() => { /* non-fatal */ });
  }, []);

  if (!info) return null;

  const g = info.git || {};
  const lastUpdate = info.last_update_at;
  const overridden = info.app_version_code && info.app_version !== info.app_version_code;

  const startEdit = () => {
    setDraft(info.app_version || "");
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft(""); };
  const save = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/system/version`, { version: next });
      setInfo({ ...info, app_version: r.data.app_version });
      setEditing(false);
      setDraft("");
      toast.success(r.data.source === "code"
        ? `Reverted to build version v${r.data.app_version}`
        : `Version set to v${r.data.app_version}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update version");
    } finally {
      setSaving(false);
    }
  };
  const revert = () => save("");   // empty string tells backend to clear override

  const fmtRel = (iso) => {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return new Date(iso).toLocaleString();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  return (
    <div
      className="rounded-lg border border-border bg-secondary/20 px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm"
      data-testid="build-info-chip"
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Version</span>
        {editing ? (
          <>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="1.2.0"
              autoFocus
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === "Enter") save(draft);
                if (e.key === "Escape") cancelEdit();
              }}
              className="font-mono text-sm w-32 h-7 px-2 rounded border border-input bg-background outline-none focus:ring-2 focus:ring-ring"
              data-testid="build-info-version-input"
            />
            <button
              onClick={() => save(draft)}
              disabled={saving || !draft.trim()}
              className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary text-green-600 disabled:opacity-40"
              title="Save"
              data-testid="build-info-version-save"
            >
              <Check size={14} weight="bold" />
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary text-muted-foreground"
              title="Cancel"
              data-testid="build-info-version-cancel"
            >
              <X size={14} weight="bold" />
            </button>
          </>
        ) : (
          <>
            <span className="font-mono font-semibold" data-testid="build-info-version">
              v{info.app_version}
            </span>
            {overridden && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200"
                title={`Build shipped v${info.app_version_code}; override set from Settings`}
                data-testid="build-info-version-override-badge"
              >
                override
              </span>
            )}
            {isAdmin && (
              <button
                onClick={startEdit}
                className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                title="Edit version"
                data-testid="build-info-version-edit"
              >
                <PencilSimple size={12} />
              </button>
            )}
            {isAdmin && overridden && (
              <button
                onClick={revert}
                className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                title={`Revert to build version (v${info.app_version_code})`}
                data-testid="build-info-version-revert"
              >
                <ArrowCounterClockwise size={12} />
              </button>
            )}
          </>
        )}
      </div>
      {g.short_sha && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Commit</span>
          <span
            className="font-mono text-xs px-1.5 py-0.5 rounded bg-card border border-border"
            title={g.sha || ""}
            data-testid="build-info-sha"
          >
            {g.short_sha}
          </span>
          {g.branch && g.branch !== "main" && (
            <span className="text-xs text-muted-foreground font-mono">@ {g.branch}</span>
          )}
          {g.is_dirty && (
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
              title="Working tree has uncommitted changes"
              data-testid="build-info-dirty"
            >
              modified
            </span>
          )}
        </div>
      )}
      {g.commit_at && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Committed</span>
          <span className="font-mono text-xs" title={g.commit_at} data-testid="build-info-commit-at">
            {fmtRel(g.commit_at)}
          </span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Last update</span>
        <span
          className="font-mono text-xs"
          title={lastUpdate || ""}
          data-testid="build-info-last-update"
        >
          {lastUpdate ? fmtRel(lastUpdate) : "never (still on initial install)"}
        </span>
      </div>
      {g.commit_subject && (
        <div className="basis-full text-xs text-muted-foreground italic truncate" title={g.commit_subject}>
          &ldquo;{g.commit_subject}&rdquo;
        </div>
      )}
    </div>
  );
};

export default BuildInfoChip;
